//src/pages/Home.tsx`

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';
import { LoginForm } from '../components/auth/LoginForm.tsx';
import { RegisterForm } from '../components/auth/RegisterForm.tsx';
import { PageHeader } from '../components/PageHeader.tsx';
import { Footer } from '../components/Footer.tsx';
import { ImagePreviewModal } from '../components/modals/ImagePreviewModal.tsx';
import './Home.css';

type Tab = 'login' | 'register';

export const Home: React.FC = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab: Tab = tabParam === 'register' ? 'register' : 'login';
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [showDashboardPreview, setShowDashboardPreview] = useState(false);
  const [showPlayPreview, setShowPlayPreview] = useState(false);
  const [showPieceCatalogPreview, setShowPieceCatalogPreview] = useState(false);
  const [showVariantCreatorPreview, setShowVariantCreatorPreview] = useState(false);
  const [showAISettingsPreview, setShowAISettingsPreview] = useState(false);
  const [showLeaderboardPreview, setShowLeaderboardPreview] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleAuthSuccess = () => {
    // Redirect to dashboard after successful login/register
    navigate('/dashboard');
  };

  // If already logged in, redirect to dashboard
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="home-page">
      <div className="home-main">
        <PageHeader
          icon="♟️"
          title="Expanded Chess"
          description="Play chess with expanded variants and custom pieces"
        />

        <div className="home-content">
        <div className="features-section">
          <h2>Features</h2>
          <div className="feature-list">
            <div className="feature-item">
              <span className="feature-icon">🏠</span>
              <div>
                <h3>Dashboard</h3>
                <p>Your central hub • Select an option to get started • <a href="#" className="preview-link" onClick={(e) => { e.preventDefault(); setShowDashboardPreview(true); }}>See preview</a></p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🎮</span>
              <div>
                <h3>Play</h3>
                <p>Play chess online with friends, against AI opponents, or practice locally • Multiple board sizes and variants available • <a href="#" className="preview-link" onClick={(e) => { e.preventDefault(); setShowPlayPreview(true); }}>See preview</a></p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🌐</span>
              <div>
                <h3>Multiplayer</h3>
                <p>Create games, host matches, join games, and challenge with others online</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📚</span>
              <div>
                <h3>Piece Catalog</h3>
                <p>Explore 36 unique pieces across 6 classes • View values and movement patterns • <a href="#" className="preview-link" onClick={(e) => { e.preventDefault(); setShowPieceCatalogPreview(true); }}>See preview</a></p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🎨</span>
              <div>
                <h3>Variant Creator</h3>
                <p>Create and save your own custom chess variants • <a href="#" className="preview-link" onClick={(e) => { e.preventDefault(); setShowVariantCreatorPreview(true); }}>See preview</a></p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🤖</span>
              <div>
                <h3>AI Settings</h3>
                <p>Configure AI opponent difficulty, behavior, and playing style • <a href="#" className="preview-link" onClick={(e) => { e.preventDefault(); setShowAISettingsPreview(true); }}>See preview</a></p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🏆</span>
              <div>
                <h3>Leaderboard</h3>
                <p>See all players ratings and rankings across all Expanded Chess variants • <a href="#" className="preview-link" onClick={(e) => { e.preventDefault(); setShowLeaderboardPreview(true); }}>See preview</a></p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">👤</span>
              <div>
                <h3>My Profile</h3>
                <p>View your account details, membership rewards, stats and game history</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📄</span>
              <div>
                <h3>Documents</h3>
                <p>Download Expanded Chess Manual with detailed explanation of Expanded Chess</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">❓</span>
              <div>
                <h3>Help</h3>
                <p>Find answers, privacy policy, terms of service, and ways to contact Expanded Chess</p>
              </div>
            </div>
          </div>
        </div>

        <div className="home-logo-section">
          <img 
            src="/ExpandedChesslogo001.png" 
            alt="Expanded Chess Logo" 
            className="home-logo"
          />
        </div>

        <div className="auth-section">
          <div className="auth-tabs">
            <button
              className={`tab-button ${activeTab === 'login' ? 'active' : ''}`}
              onClick={() => setActiveTab('login')}
            >
              Sign In
            </button>
            <button
              className={`tab-button ${activeTab === 'register' ? 'active' : ''}`}
              onClick={() => setActiveTab('register')}
            >
              Create Account
            </button>
          </div>

          <div className="auth-tab-content">
            {activeTab === 'login' ? (
              <LoginForm
                onSuccess={handleAuthSuccess}
                onSwitchToRegister={() => setActiveTab('register')}
              />
            ) : (
              <RegisterForm
                onSuccess={handleAuthSuccess}
                onSwitchToLogin={() => setActiveTab('login')}
              />
            )}
          </div>
        </div>
      </div>
      </div>
      <Footer />

      <ImagePreviewModal
        isOpen={showDashboardPreview}
        imageSrc="/dashboard-preview.png"
        imageAlt="Dashboard Preview"
        title="Dashboard Preview"
        onClose={() => setShowDashboardPreview(false)}
      />

      <ImagePreviewModal
        isOpen={showPlayPreview}
        imageSrc="/play-preview.png"
        imageAlt="Play Preview"
        title="Play Preview"
        onClose={() => setShowPlayPreview(false)}
      />

      <ImagePreviewModal
        isOpen={showPieceCatalogPreview}
        imageSrc="/piece-catalog-preview.png"
        imageAlt="Piece Catalog Preview"
        title="Piece Catalog Preview"
        onClose={() => setShowPieceCatalogPreview(false)}
      />

      <ImagePreviewModal
        isOpen={showVariantCreatorPreview}
        imageSrc="/variant-creator-preview.png"
        imageAlt="Variant Creator Preview"
        title="Variant Creator Preview"
        onClose={() => setShowVariantCreatorPreview(false)}
      />

      <ImagePreviewModal
        isOpen={showAISettingsPreview}
        imageSrc="/ai-settings-preview.png"
        imageAlt="AI Settings Preview"
        title="AI Settings Preview"
        onClose={() => setShowAISettingsPreview(false)}
      />

      <ImagePreviewModal
        isOpen={showLeaderboardPreview}
        imageSrc="/leaderboard-preview.png"
        imageAlt="Leaderboard Preview"
        title="Leaderboard Preview"
        onClose={() => setShowLeaderboardPreview(false)}
      />
    </div>
  );
};
